import type { PortfolioQuery } from "@/v2-backoffice-client/portfolio/__types__/portfolio.query"
import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import {
  buildCustomerActivityTabLink,
  buildCustomerViewLink,
} from "@/v2-backoffice-client/customer/customer.links"
import { LICENSE_STATUS_TRIAL } from "@/v2-backoffice-client/license/license.constants"
import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import {
  getScoreLevel,
  getTrendLevel,
  type ScoreLevel,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  deriveScopeAppMix,
  deriveUsageScopeSummary,
  type ChurnRiskLevel,
  type UsageScopeMember,
  type UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  deriveAppInsights,
  deriveUsageInsights,
  type UsageInsight,
} from "@/v2-backoffice-client/usage/scope/usage.scope.insights"
import {
  RENEWAL_OVERDUE_GRACE_DAYS,
  RENEWAL_URGENT_DAYS,
  type PortfolioHealthBreak,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"

// The Portfolio's CORE model: the customer summary every section derives
// from, the scope/facet filters, the fleet rollup, and the deadline/link
// display helpers shared across sections. Topical derivations live beside
// it: portfolio.health.utils (risk/segments/idle/mix/movers),
// portfolio.plays.utils (the playbook), portfolio.area.utils (interest-area
// intelligence and revenue attribution), portfolio.audience.utils
// (DAU/WAU/MAU) and portfolio.geo.utils (municipality reads).

/** One customer group as getUsagePortfolio returns it. */
export type PortfolioCustomer = PortfolioQuery["getUsagePortfolio"][number]

/**
 * The nearest money conversation for a customer: contract renewal or trial
 * expiry, whichever is closest. Recently-lapsed dates surface as overdue
 * (negative daysUntil) for {@link RENEWAL_OVERDUE_GRACE_DAYS} days — a
 * renewal that slipped last week is hotter, not stale; older past dates are
 * dropped as CRM noise.
 */
export type PortfolioDeadline = {
  kind: "renewal" | "trial"
  /** YYYY-MM-DD */
  date: string
  /** Negative = overdue by that many days. */
  daysUntil: number
}

/**
 * One customer's derived portfolio entry: the same scope summary, app mix,
 * and plays the customer panel's Activity tab computes — from the same
 * functions, so the Portfolio page and the panel never disagree — plus the
 * license meta (name/seats/status/deadline, sourced from lic.Licenses)
 * used for prioritization.
 */
export type PortfolioCustomerSummary = {
  externalCustomerId: string
  name: string
  stage: string | null
  /** HubSpot company owner's display name — the CRM book this account belongs to. */
  owner: string | null
  /** Held licenses as a display string ("Placepoint Premium, Propcloud Takst"). */
  plan: string | null
  userLicenses: number | null
  /**
   * Governing license status (lic.LicenseStatuses codes; a trial outranks
   * everything). 0 = the customer holds no license, null = rows predating
   * the field.
   */
  licenseStatusCode: number | null
  /** Soonest trial end (YYYY-MM-DD), regardless of the deadline grace cap. */
  trialEndsAt: string | null
  deadline: PortfolioDeadline | null
  /**
   * Billed the last 12 months (invoice mirror via the portfolio proc) —
   * the money the churn risk puts at stake. Null without a linked billing
   * account or invoice history.
   */
  billed12M: number | null
  primaryCurrency: string | null
  /** Fenerum's account uuid — the "Open in Fenerum" handle; null when unlinked. */
  billingAccountUuid: string | null
  members: UsageScopeMember[]
  summary: UsageScopeSummary
  appMix: UsageAppMixEntry[]
  insights: UsageInsight[]
}

/**
 * Applies the Portfolio's global scope filters to the raw customer groups
 * BEFORE any derivation, so KPIs, charts, plays and maps all agree with the
 * filter bar. Customers left with no members (all excluded) drop out.
 */
export function filterPortfolioCustomers(
  rows: PortfolioCustomer[],
  filter: {
    customerIds: string[]
    excludeCustomerIds: string[]
    excludeUserIds: number[]
    /** HubSpot company owners to keep — "my book"; empty = all. */
    owners: string[]
  },
): PortfolioCustomer[] {
  const include = new Set(filter.customerIds)
  const excludeCustomers = new Set(filter.excludeCustomerIds)
  const excludeUsers = new Set(filter.excludeUserIds)
  const owners = new Set(filter.owners)

  return rows
    .filter(
      (row) =>
        (include.size === 0 || include.has(row.ExternalCustomerId)) &&
        !excludeCustomers.has(row.ExternalCustomerId) &&
        (owners.size === 0 || (row.Owner != null && owners.has(row.Owner))),
    )
    .map((row) =>
      excludeUsers.size === 0
        ? row
        : {
            ...row,
            Members: row.Members.filter(
              (member) => !excludeUsers.has(member.UserId),
            ),
          },
    )
    .filter((row) => row.Members.length > 0)
}

/**
 * Derives every customer's scope summary + plays from the portfolio groups.
 * Note: the fleet payload carries no per-customer daily rows, so per-app
 * TRENDS are unknown here (the customer panel derives them from its daily
 * query); the trend-less product plays (e.g. v1-heavy → v2 demo) still fire.
 */
export function derivePortfolioCustomers(
  rows: PortfolioCustomer[],
): PortfolioCustomerSummary[] {
  return rows.map((row) => {
    const members: UsageScopeMember[] = row.Members
    const summary = deriveUsageScopeSummary(members)
    const appMix = deriveScopeAppMix(members)
    const insights = [
      ...deriveAppInsights(appMix),
      ...deriveUsageInsights(summary),
    ]
    return {
      externalCustomerId: row.ExternalCustomerId,
      name: row.Name,
      stage: row.Stage ?? null,
      owner: row.Owner ?? null,
      plan: row.Plan ?? null,
      userLicenses: row.UserLicenses ?? null,
      licenseStatusCode: row.LicenseStatusCode ?? null,
      trialEndsAt:
        row.TrialExpiresAt != null
          ? String(row.TrialExpiresAt).slice(0, 10)
          : null,
      deadline: deriveDeadline(row),
      billed12M: row.Billed12M ?? null,
      primaryCurrency: row.PrimaryCurrency ?? null,
      billingAccountUuid: row.BillingAccountUuid ?? null,
      members,
      summary,
      appMix,
      insights,
    }
  })
}

/**
 * The facet filters (churn risk, engagement level, license status) —
 * applied AFTER derivation because the levels are derived reads, unlike
 * the id scopes `filterPortfolioCustomers` applies to the raw rows. Empty
 * selections pass everything. The license facet matches the GOVERNING
 * status (null — rows predating the field — never matches a selection).
 */
export function filterPortfolioSummaries(
  customers: PortfolioCustomerSummary[],
  filter: {
    churnLevels: ChurnRiskLevel[]
    scoreLevels: ScoreLevel[]
    licenseStatusCodes: number[]
  },
): PortfolioCustomerSummary[] {
  if (
    filter.churnLevels.length === 0 &&
    filter.scoreLevels.length === 0 &&
    filter.licenseStatusCodes.length === 0
  ) {
    return customers
  }
  const churn = new Set(filter.churnLevels)
  const score = new Set(filter.scoreLevels)
  const license = new Set(filter.licenseStatusCodes)
  return customers.filter(
    (customer) =>
      (churn.size === 0 || churn.has(customer.summary.churnRisk.level)) &&
      (score.size === 0 ||
        score.has(getScoreLevel(customer.summary.avgScore))) &&
      (license.size === 0 ||
        (customer.licenseStatusCode != null &&
          license.has(customer.licenseStatusCode))),
  )
}

/**
 * The whole book of business rolled up as one scope — the same derivation
 * as any team/customer summary, run over every customer-linked seat. Feeds
 * the KPI header (seat bands, activation, trend, avg score, top areas).
 */
export function derivePortfolioFleet(
  customers: PortfolioCustomerSummary[],
): UsageScopeSummary {
  return deriveUsageScopeSummary(
    customers.flatMap((customer) => customer.members),
  )
}

/**
 * The scatter's color break for one customer: churn risk, with high risk
 * split by whether usage is STILL falling — "critical" is the call-today
 * pile (at risk and deteriorating), plain "high" has at least stabilized.
 */
export function getHealthBreak(
  customer: PortfolioCustomerSummary,
): PortfolioHealthBreak {
  const level = customer.summary.churnRisk.level
  if (level === "high") {
    const trend = getTrendLevel(customer.summary.trendPct)
    return trend === "falling" || trend === "falling-hard" ? "critical" : "high"
  }
  if (level === "medium") return "medium"
  if (level === "low") return "low"
  return "new"
}

/**
 * Customers with an upcoming renewal/trial deadline, soonest first — the CS
 * heartbeat list. No horizon cap: the component decides how many to show.
 */
export function deriveUpcomingRenewals(
  customers: PortfolioCustomerSummary[],
): PortfolioCustomerSummary[] {
  return customers
    .filter((customer) => customer.deadline != null)
    .sort((a, b) => (a.deadline?.daysUntil ?? 0) - (b.deadline?.daysUntil ?? 0))
}

/** The trials KPI: how many customers are trialing, and how urgently. */
export type PortfolioTrialPulse = {
  /** Customers whose governing license status is Trial. */
  count: number
  /** Of those, trials past their end date (however long ago). */
  overdue: number
  /** Of those, trials ending within {@link RENEWAL_URGENT_DAYS}. */
  endingSoon: number
}

/**
 * Counts trialing customers from the governing status plus the RAW trial
 * end date — not the deadline, which drops dates more than the grace
 * window past due; a trial three months overdue is exactly what this KPI
 * exists to expose.
 */
export function deriveTrialPulse(
  customers: PortfolioCustomerSummary[],
): PortfolioTrialPulse {
  const pulse: PortfolioTrialPulse = { count: 0, overdue: 0, endingSoon: 0 }
  for (const customer of customers) {
    if (customer.licenseStatusCode !== LICENSE_STATUS_TRIAL) continue
    pulse.count += 1
    if (customer.trialEndsAt == null) continue
    const daysUntil = Math.ceil(
      (new Date(customer.trialEndsAt).getTime() - Date.now()) / ONE_DAY_IN_MS,
    )
    if (daysUntil < 0) pulse.overdue += 1
    else if (daysUntil <= RENEWAL_URGENT_DAYS) pulse.endingSoon += 1
  }
  return pulse
}

/**
 * Deep link from a Portfolio row into the customer panel's Activity tab —
 * the same target the customers table's usage cells use, so investigation
 * always lands on the page that explains the number.
 */
export function buildCustomerActivityLink(externalCustomerId: string) {
  return buildCustomerViewLink(externalCustomerId, "sign-ins")
}

/**
 * Deep link from an MCP row into the customer panel's Placepoint MCP tab —
 * the same report at the customer's scope.
 */
export function buildCustomerMcpLink(externalCustomerId: string) {
  return buildCustomerActivityTabLink(externalCustomerId, "placepoint-mcp")
}

/**
 * Deep link from a name in a Portfolio play to that person's Usage tab.
 * Page context: a plain `?v=` opens the sheet over the page.
 */
export function buildUserUsageLink(userId: number) {
  return `?v=user:${userId}&user-view-tabs-tab=usage`
}

/**
 * Countdown text for a deadline: "Renews in 45 d" / "Trial ends today" /
 * "Renewal 12 d overdue".
 */
export function formatDeadline(deadline: PortfolioDeadline) {
  if (deadline.daysUntil < 0) {
    const days = Math.abs(deadline.daysUntil)
    return deadline.kind === "renewal"
      ? `Overdue ${days} d`
      : `Trial ended ${days} d`
  }
  const when = deadline.daysUntil === 0 ? "today" : `in ${deadline.daysUntil} d`
  return deadline.kind === "renewal" ? `Renews ${when}` : `Trial ends ${when}`
}

/** Badge tone for a deadline: overdue = critical, near = warning, else neutral. */
export function deadlineBadgeVariant(
  deadline: PortfolioDeadline,
): BadgeVariant {
  if (deadline.daysUntil < 0) return "critical"
  if (deadline.daysUntil <= RENEWAL_URGENT_DAYS) return "warning"
  return "neutral"
}

/**
 * Nearest renewal/trial deadline. Date-only precision (both columns are
 * DATEs); today counts as 0 days, recently-past dates stay visible as overdue
 * (see {@link PortfolioDeadline}), older ones are dropped as stale.
 *
 * Both dates are sourced from lic.Licenses (the Fenerum-mirrored renewal
 * clock and the operator-managed trial clock), with the frozen legacy
 * claim columns on acc.Customers as fallback — so this returns real values
 * exactly for customers the license domain covers.
 */
function deriveDeadline(row: PortfolioCustomer): PortfolioDeadline | null {
  const candidates: PortfolioDeadline[] = []
  const kinds = [
    { kind: "renewal", value: row.ContractRenewsAt },
    { kind: "trial", value: row.TrialExpiresAt },
  ] as const
  for (const { kind, value } of kinds) {
    if (!value) continue
    const date = String(value).slice(0, 10)
    const daysUntil = Math.ceil(
      (new Date(date).getTime() - Date.now()) / ONE_DAY_IN_MS,
    )
    if (daysUntil < -RENEWAL_OVERDUE_GRACE_DAYS) continue
    candidates.push({ kind, date, daysUntil })
  }
  return candidates.sort((a, b) => a.daysUntil - b.daysUntil)[0] ?? null
}
